import {Component, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {saveRecord} from '../store/list/actions';
import {getRecords} from '../store/categories/selectors';
import {getRecords as getBrands} from '../store/brands/selectors';
import {Prodotto} from '../../../models/Prodotto';
import {hasCustomProductsPermission} from '../../../core/store/account/selectors';

@Component({
  styles: [``],
  template: `
    <div class="modal-header">
      <h4 class="modal-title">{{ record ? 'Modifica' : 'Nuovo' }} prodotto</h4>
    </div>
    <div class="modal-body">
      <form [formGroup]="form" (submit)="submitHandler($event)">
        <div class="form-group row">
          <label class="col-form-label">Linea *</label>
          <div class="control-input">
            <ng-select
              [items]="categories"
              formControlName="category_id"
              bindLabel="name"
              bindValue="id"
              [closeOnSelect]="true"
            >
              <ng-template ng-option-tmp let-item="item" let-item$="item$" let-index="index">
                <div class="CategoryBox">
                  <div class="CategoryBox_Color" [style.background]="item.color"></div>
                  <div class="CategoryBox_Text">{{item.name}}</div>
                </div>
              </ng-template>
            </ng-select>
          </div>
        </div>

        <div class="form-group row">
          <label class="col-form-label">Brand</label>
          <div class="control-input">
            <ng-select
              [items]="brands"
              formControlName="brand_id"
              bindLabel="name"
              bindValue="id"
              [closeOnSelect]="true"
            ></ng-select>
          </div>
        </div>

        <div class="row form-group">
          <label class="col-form-label">Nome *</label>
          <div class="control-input">
            <input class="form-control" type="text" formControlName="name" [ngClass]="{'is-invalid': errors?.name }">
            <span *ngIf="errors?.name"
                  class="invalid-feedback"
                  role="alert">
                    {{ errors?.name[0] }}
                </span>
          </div>
        </div>
        <div class="form-group row">
          <label class="col-form-label">Prezzo di vendita (iva inc.) *</label>
          <div class="control-input">
            <div class="input-group">
              <div class="input-group-prepend"><span class="input-group-text">&euro;</span></div>
              <input class="form-control" type="number" step="0.1" formControlName="price" [ngClass]="{'is-invalid': errors && errors?.price }">
            </div>
            <span *ngIf="errors?.price" class="invalid-feedback" role="alert">{{ errors?.price[0] }}</span>
          </div>
        </div>

        <div class="form-group row">
          <label class="col-form-label">Prezzo di acquisto (iva esc.)</label>
          <div class="control-input">
            <div class="input-group">
              <div class="input-group-prepend"><span class="input-group-text">&euro;</span></div>
              <input class="form-control" type="number" step="0.01" min="0" formControlName="prezzo_acquisto" [ngClass]="{'is-invalid': errors && errors?.prezzo_acquisto }">
            </div>
            <span *ngIf="errors?.prezzo_acquisto" class="invalid-feedback" role="alert">{{ errors?.prezzo_acquisto[0] }}</span>
          </div>
        </div>

        <div class="form-group row">
          <label class="col-form-label">Istruzioni d'uso</label>
          <div class="control-input">
            <textarea class="form-control" rows="4" formControlName="usage"></textarea>
            <p><small style="line-height: 1em">Come si applica il prodotto: le indicazioni da dare al cliente.</small></p>
          </div>
        </div>

        <div class="form-group row" *ngCan="'magazzino'">
          <label class="col-form-label">Rimanenza</label>
          <div class="control-input">
            <input class="form-control" type="number" step="1" formControlName="giacenza">
            <p><small style="line-height: 1em">Pezzi presenti in magazzino. Può essere negativa: finché non fai il primo censimento le vendite la portano sotto zero.</small></p>
          </div>
        </div>

        <div class="form-group row" *ngCan="'magazzino'">
          <label class="col-form-label">Giacenze</label>
          <div class="control-input">
            <input class="form-control" type="number" step="1" min="0" formControlName="giacenza_minima">
            <p><small style="line-height: 1em">Quantità minima da tenere in magazzino: quando la rimanenza la raggiunge il prodotto viene segnalato da riordinare. Lascia 0 per non impostarla.</small></p>
          </div>
        </div>

        <div class="form-group row" *ngIf="hasCustomProductsPermission$ | async">
          <label class="col-form-label">Solo vendita</label>
          <div class="control-input">
            <div>
              <span class="switch">
                  <label>
                      <input name="enabled" formControlName="only_shop" type="checkbox" checked="checked" value="1">
                      <span></span>
                  </label>
              </span>
            </div>
            <p><small style="line-height: 1em">Se la casella è spuntata il prodotto non sarà selezionabile all'interno della scheda trattamento</small></p>
          </div>
        </div>

      </form>

    </div>
    <div class="modal-footer">
      <button class="btn btn-default" (click)="modal.dismiss()">Chiudi</button>
      <button class="btn btn-primary" [disabled]="form.invalid" (click)="submitHandler($event)">Salva</button>
    </div>
  `
})

export class ItemModalComponent implements OnInit {
  public userId = null;
  public record: Prodotto | null = null;
  public errors = null;
  form: FormGroup;
  categories = [];
  brands = [];

  hasCustomProductsPermission$ = this.store.select(hasCustomProductsPermission);

  constructor(
    fb: FormBuilder,
    public modal: NgbActiveModal,
    private store: Store
  ) {

    this.form = fb.group({
      id: fb.control(null, []),
      user_id: fb.control(null, []),
      category_id: fb.control(null, []),
      brand_id: fb.control(null, []),
      price: fb.control(null, [Validators.required]),
      prezzo_acquisto: fb.control(null, [Validators.min(0)]),
      name: fb.control(null, [Validators.required]),
      // Colonna `usage`, che esisteva già a schema e in `$fillable` ma non era
      // mai stata esposta: nessuna migrazione, solo il campo che mancava.
      usage: fb.control(null),
      only_shop: fb.control(0),
      giacenza: fb.control(0),
      giacenza_minima: fb.control(0)
    });

    this.store.select(getRecords).subscribe(categories => {
      this.categories = categories;
    });

    this.store.select(getBrands).subscribe(brands => {
      this.brands = brands || [];
    });
  }

  ngOnInit(): void {
    if (this.record) {
      this.form.patchValue(this.record);
    }
  }

  submitHandler(e) {
    this.store.dispatch(saveRecord({record: this.form.value}));
  }
}
